import { Typography, Box, Button, Pagination } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProductListBySearchNameQuery } from '../features/api/openFoodFacts/apiFoodFactsSlice';
import { useGetRatingsByProductListQuery } from '../features/api/rest/apiReviewsSlice';
import { useListFavoritesQuery } from '../features/api/rest/apiFavoritesSlice';
import ProductCard from './ProductCard';
import Spinner from './Spinner';

function ProductList() {
  const [searchParams] = useSearchParams();
  const searchInput = searchParams.get('search');
  const currentPage = parseInt(searchParams.get('page'), 10) || 1;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    productsCount,
    productList,
    eans,
    isSuccess: isGetProductListBySearchNameSuccess,
  } = useGetProductListBySearchNameQuery(
    { searchInput, currentPage },
    {
      selectFromResult: (result) => ({
        ...result,
        productsCount: result.data?.count,
        productList: result.data?.products,
        eans: result.data?.products?.map((product) => product._id), // eslint-disable-line no-underscore-dangle
      }),
    }
  );
  const { ratings, isSuccess: isGetRatingsSuccess } = useGetRatingsByProductListQuery(
    { eans: isGetProductListBySearchNameSuccess ? eans.join(',') : skipToken, psize: 50 },
    {
      selectFromResult: (result) => ({
        ...result,
        ratings: result.data?.results?.map((rating) => ({
          ean: rating.ean_code,
          average: rating.average,
          count: rating.star1 + rating.star2 + rating.star3 + rating.star4 + rating.star5,
        })),
      }),
      skip: !isGetProductListBySearchNameSuccess,
    }
  );

  const { favoritesData } = useListFavoritesQuery(
    { eans: isGetProductListBySearchNameSuccess ? eans.join(',') : skipToken, psize: 50 },
    {
      selectFromResult: (result) => ({
        ...result,
        favoritesData: result.currentData?.results,
      }),

      skip: !isGetProductListBySearchNameSuccess,
    }
  );

  const handleReturn = () => {
    navigate('/');
  };

  const handlePageChange = (nextPage) => {
    searchInput ? navigate(`/products?search=${searchInput}&page=${nextPage}`) : navigate(`/products?page=${nextPage}`);
  };

  const pagination = productsCount > 50 && <Pagination sx={{ my: 2 }} count={Math.ceil(productsCount / 50)} page={currentPage} onChange={(event, newPage) => handlePageChange(newPage)} />;

  if (!productList) return <Spinner />;

  return (
    <>
      <Typography variant="h5">{t('products.research.found', { count: parseInt(productsCount, 10) })}</Typography>
      <Box>
        {productsCount === 0 && (
          <Button variant="contained" onClick={handleReturn} sx={{ mt: 3 }}>
            {t('products.research.notFound.searchAnotherProduct')}
          </Button>
        )}
        {pagination}
        <section id="Products__Grid">
          {productList &&
            productList.map((product) => (
              <ProductCard
                ean={product._id} // eslint-disable-line no-underscore-dangle
                image={product?.image_front_small_url}
                name={product?.product_name}
                key={product._id} // eslint-disable-line no-underscore-dangle
                rating={ratings?.find((rating) => rating.ean === product._id)} // eslint-disable-line no-underscore-dangle
                shouldDisplayRatings={isGetRatingsSuccess}
                favorite={favoritesData?.find((favorite) => favorite.ean_code === product._id)?.favorite} // eslint-disable-line no-underscore-dangle
              />
            ))}
        </section>
        {pagination}
      </Box>
    </>
  );
}

export default ProductList;
